import { useMemo } from 'react';
import { createDecoratedExperienceEvent } from '@/features/events/lib/createDecoratedExperienceEvent';
import type {
  DecoratedExperienceEvent,
  RawEvent,
} from '@/features/events/types';
import type { PageExperience } from '@/features/experiences/types';
import { EventListDisplay } from '@/types/gql.generated';
import { sortByStartDate } from '../lib/sortByStartDate.lib';
import { filterUpcomingEvents } from '../lib/upcomingEvents.lib';

type ReturnValue = {
  decoratedExperienceEvents: DecoratedExperienceEvent[];
};

type ExperienceEvents = RawEvent[];

export const useCreateDecoratedExperienceEvents = (
  experience: PageExperience | undefined,
  events: ExperienceEvents,
  displayTimezone: string
): ReturnValue => {
  // Extract only the parts of the experience we need to calculate events.
  // This is to prevent unnecessary re-calculation of events when unrelated
  // properties on the experience change, like the hue.
  const partialExperience = useMemo(
    () => ({
      blocks: experience?.blocks,
      interactionSlots: experience?.interactionSlots,
      interactionApps: experience?.interactionApps,
      eventListDisplay: experience?.eventListDisplay,
    }),
    [
      experience?.blocks,
      experience?.interactionSlots,
      experience?.interactionApps,
      experience?.eventListDisplay,
    ]
  );

  const decoratedExperienceEvents = useMemo<DecoratedExperienceEvent[]>(() => {
    const decoratedEvents =
      events?.map((event) =>
        createDecoratedExperienceEvent(
          event,
          partialExperience,
          displayTimezone
        )
      ) ?? [];

    if (partialExperience.eventListDisplay === EventListDisplay.Upcoming) {
      return filterUpcomingEvents(decoratedEvents, displayTimezone);
    }
    return sortByStartDate(decoratedEvents);
  }, [events, partialExperience, displayTimezone]);

  return { decoratedExperienceEvents };
};
